import { Tenant } from '../../services/tenant.service';

export interface CassetteConfigItem {
  [cassetteKey: string]: {
    maxCapacity: number;
    reloadThreshold?: number;
    reloadWarningThreshold?: number;
    reloadCriticalThreshold?: number;
    denomination: number;
  };
}

export interface HostConfig {
  surchargeAmount: string;
  withdrawalIncrementAmount: string;
  maximumWithdrawalAmount: string;
  healthCheckIntervalHours?: string;
  cassetteConfig: CassetteConfigItem[];
}

export interface CassetteInfo {
  maxCapacity?: number | null;
  reloadThreshold?: number | null;
  reloadWarningThreshold?: number | null;
  reloadCriticalThreshold?: number | null;
  notesLoaded?: number | null;
  noteCount?: number | null;
  dispensed?: number | null;
}

export interface CassetteManagement {
  cassette1?: CassetteInfo;
  cassette2?: CassetteInfo;
  cassette3?: CassetteInfo;
  cassette4?: CassetteInfo;
  totalDispensedSinceStart?: number;
  lastReloadDate?: string;
  reloadPending?: boolean;
}

export interface DeviceInfo {
  deviceType: string;
  applicationVersion: string;
  vendorName: string;
  applicationName: string;
  tsn: string;
}

export interface TimeInfo {
  dstIndicator: string;
  timeZone: string;
}

export interface EmvTag {
  tag: string;
  value: string;
}

export interface CurrentAuthorizationRequest {
  saleAmount?: string;
  surchargeAmount?: string;
  pan4?: string;
  sourceAccount?: string;
  tipAmount?: string;
  operationCode?: string;
  cashbackAmount?: string;
  merchantTerminalId?: string;
  transactionId?: string;
  deviceInfo?: DeviceInfo;
  surchargeEnabled?: boolean;
  totalAmount?: string;
  timeInfo?: TimeInfo;
  flow_id?: string;
  transactionAmount?: string;
  tenantId?: string;
  emvTags?: EmvTag[];
  p2peTrack2?: string;
  destinationAccount?: string;
  encryptedPinBlock?: string;
}

export interface SidebarMerchant {
  merchantUuid: string;
  name: string;
  tenantId: string;
}

export interface AdminMerchant extends SidebarMerchant {
  description?: string;
  minInterArrivalMs?: string;
  hostConfig?: HostConfig;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface SidebarTerminal {
  terminalUuid: string;
  terminalId: string;
  type: 'host' | 'merchant';
  tenantId: string;
  merchantUuid: string;
  state?: string;
  priority?: number;
  weight?: number;
  processor?: string;
  manufacturer?: string;
}

export interface AdminTerminal extends SidebarTerminal {
  merchantName?: string;
  timeZone?: string;
  processor?: string;
  createdAt?: string;
  updatedAt?: string;
  isActive?: boolean;
  assignedAt?: string;
  semaphore?: number;
  priority?: number;
  sequenceNumber?: string;
  weight?: number;
  state?: string;
  host?: string;
  manufacturer?: string;
  healthCheckTime?: number;
  cassetteManagement?: CassetteManagement;
  isReversal?: boolean;
  totalSelections?: number;
  lastStateChange?: string;
  currentTransactionId?: string;
  currentAuthorizationRequest?: CurrentAuthorizationRequest;
  mkTrKcv?: string;
  mkCgKcv?: string;
  mkTrCreatedAt?: string;
  mkCgCreatedAt?: string;
}

export interface HostConnection {
  /** Secrets Manager ARN of the stored CA certificate; returned by the API. */
  hostCertificateArn?: string;
  /** New PEM certificate to store; sent on create/replace, never returned by the API. */
  hostCertificateContent?: string;
  /** Legacy (pre ISERV-1900) cert-layer folder name; used while the feature flag is off. */
  hostCertificateFolderName?: string;
  verifyServerCertificate: boolean;
  isDefault: boolean;
  port: string;
  url: string;
  transportProtocol: string;
}

export interface Visa2Options {
  stxLrcEnabled: boolean;
  maxRetries: number;
  parity: string;
  responseTimeoutMs: number;
  ackResponseEnabled: boolean;
  eotTimeoutMs: number;
  retryDelayMs?: number;
  retransmitCount?: number;
  enqEnabled?: boolean;
  commsTimer?: number;
}

export interface CassetteReloadOptions {
  cassetteReloadDelayMinMinutes?: number;
  cassetteReloadDelayMaxMinutes?: number;
  cassetteReloadDurationMinMinutes?: number;
  cassetteReloadDurationMaxMinutes?: number;
}

export interface SidebarHost {
  hostUuid: string;
  host: string;
  tenantId: string;
  processor: string;
  manufacturer: string;
}

export interface AdminHost extends SidebarHost {
  bankIdNumber?: string;
  hostConnections?: HostConnection[];
  visa2Options?: Visa2Options;
  reversalEnabled?: boolean;
  healthCheckEnabled?: boolean;
  healthCheckIntervalHours?: string;
  keyRefreshDelaySeconds?: string;
  sessionKeyExpiryHours?: string;
  terminalProcessingDelayMs?: string;
  cassetteReloadOptions?: CassetteReloadOptions;
  type?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface AdminTreeNode {
  key: string;
  label: string;
  data: {
    type: 'tenant' | 'folder' | 'merchant' | 'terminal' | 'host';
    entity?: Tenant | AdminMerchant | AdminTerminal | AdminHost;
    tenantId?: string;
    merchantUuid?: string;
    folderType?: 'merchants' | 'hosts' | 'host-terminals' | 'merchant-terminals';
    link?: string[];
  };
  children?: AdminTreeNode[];
  leaf?: boolean;
  expanded?: boolean;
  selectable?: boolean;
}

export enum KeyWrappedBy {
  TenantKek = 'tenant-kek',
  HostMkTr = 'host-mk-tr',
  HostMkCg = 'host-mk-cg'
}

export interface AdminTenantKey {
  type: string;
  tenantId: string;
  name?: string;
  version?: number;
  tenantKekName?: string;
  clientKekName?: string;
  mkName?: string;
  algorithm?: string;
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
  keyAlias?: string;
  region?: string;
  kcv?: string;
  wrappedBy?: string;
  wrappedKeyBlock?: string;
  wrappedByKeyArn?: string;
  host?: string;
  hostTerminalId?: string;
  processor?: string;
  manufacturer?: string;
  clientKekVersion?: number | string;
  tenantKekVersion?: number | string;
  ksi?: string;
  lastModified?: string;
  ttl?: number;
  [key: string]: any;
}

export interface AdminState {
  tenants: { [tenantId: string]: Tenant };
  merchants: { [uuid: string]: AdminMerchant };
  terminals: { [uuid: string]: AdminTerminal };
  hosts: { [hostUuid: string]: AdminHost };
  tenantKeys: { [tenantId: string]: { [keyIndex: string]: AdminTenantKey } };
}
